import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/base/ScrollToTopButton';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import { articles, Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a: Article) => a.slug === slug);

  const relatedArticles = article
    ? articles
        .filter((a: Article) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
        .slice(0, 3)
    : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">该文章可能已被移除或链接失效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SEOSchema type="Organization" />
      <SEOSchema
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.coverImage,
          datePublished: article.date,
          dateModified: article.date,
          author: article.author,
        }}
      />
      <SEOSchema
        type="WebPage"
        data={{
          name: `${article.title} - 纲手本子`,
          description: article.excerpt,
          url: `https://tsunade-doujin.readdy.ai/article/${article.slug}`,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <div className="relative w-full h-[300px] md:h-[400px] rounded-xl overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2.5 py-1 bg-accent-500 text-white text-xs rounded-full font-medium">
                  {article.category}
                </span>
                {article.tags.map((tag: string) => (
                  <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200/70">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>{article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>{article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>{(article.views / 1000).toFixed(1)}k 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-heart-line text-accent-500"></i>{(article.likes / 1000).toFixed(1)}k 喜欢
                </span>
              </div>
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-share-forward-line text-accent-600"></i>
                </div>
                <h2 className="font-heading text-lg text-foreground-900">分享作品</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {['微博', '微信', 'QQ', '复制链接'].map((platform) => (
                  <button
                    key={platform}
                    className="px-4 py-2 bg-background-100 border border-background-200/70 rounded-full text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {platform}
                  </button>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="max-w-4xl mx-auto px-4 md:px-6 py-12">
              <h2 className="font-heading text-xl text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((related: Article) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-36 overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="font-heading text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{related.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
        <ScrollToTopButton />
      </div>
    </>
  );
}